<template>
  <div class="inline-flex flex-col gap-0.5">
    <!-- Label -->
    <label v-if="label" :id="labelId" class="text-xs font-semibold">
      {{ label }}
    </label>

    <!-- Dropdown -->
    <div class="relative inline-block">

      <!-- Hidden sizer to auto-width based on longest option -->
      <div class="invisible h-0 overflow-hidden flex items-center gap-2 px-3 text-sm font-medium" aria-hidden="true">
        <font-awesome-icon v-if="icon" :icon="icon" class="text-sm" />
        <span :class="{ sensitive: sensitiveLabels }">{{ longestLabel }}</span>
        <font-awesome-icon :icon="['fas', 'chevron-down']" class="text-xs" />
      </div>

      <button
        ref="trigger"
        type="button"
        role="combobox"
        :aria-expanded="isOpen"
        :aria-haspopup="'listbox'"
        :aria-labelledby="label ? labelId : undefined"
        :aria-activedescendant="isOpen && focusedIndex >= 0 ? optionId(focusedIndex) : undefined"
        class="w-full flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm font-medium transition-colors"
        :class="isDisabled ? 'text-gray-400 opacity-50' : 'text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500'"
        :disabled="isDisabled"
        @click="toggleDropdown"
        @keydown="handleTriggerKeydown"
      >
        <!-- Icon -->
        <font-awesome-icon
          v-if="icon"
          :icon="icon"
          class="text-gray-600 text-sm"
        />

        <!-- Selected Value -->
        <span :class="{ sensitive: sensitiveLabels && modelValue !== null }">{{ displayValue }}</span>

        <!-- Chevron -->
        <font-awesome-icon
          :icon="['fas', 'chevron-down']"
          class="text-gray-500 text-xs ml-auto"
          :class="{ 'rotate-180': isOpen }"
        />
      </button>

      <!-- Dropdown Menu -->
      <Teleport to="body">
      <div
        v-if="isOpen"
        ref="listbox"
        role="listbox"
        :aria-labelledby="label ? labelId : undefined"
        class="fixed bg-white border border-gray-200 rounded-lg shadow-lg z-50"
        :style="dropdownStyle"
      >
        <div class="py-1 max-h-60 overflow-y-auto">
          <div
            v-if="clearable"
            :id="optionId(-1)"
            role="option"
            :aria-selected="modelValue === null"
            class="w-full text-left px-4 py-2 text-sm cursor-pointer transition-colors"
            :class="[
              modelValue === null ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-400',
              focusedIndex === -1 && isOpen ? 'bg-gray-100' : ''
            ]"
            @click="selectOption({ value: null, label: placeholder })"
            @mouseenter="focusedIndex = -1"
          >
            {{ placeholder }}
          </div>
          <div
            v-for="(option, index) in options"
            :key="option.value"
            :id="optionId(index)"
            role="option"
            :aria-selected="option.value === modelValue"
            class="w-full text-left px-4 py-2 text-sm cursor-pointer transition-colors"
            :class="[
              option.value === modelValue
                ? 'bg-primary-50 text-primary-700 font-medium'
                : 'text-gray-700',
              focusedIndex === index ? 'bg-gray-100' : ''
            ]"
            @click="selectOption(option)"
            @mouseenter="focusedIndex = index"
          >
            <span :class="{ sensitive: sensitiveLabels }">{{ option.label }}</span>
          </div>
        </div>
      </div>
      </Teleport>

    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

let uid = 0;

export default {
  name: 'FilterDropdown',
  components: {
    FontAwesomeIcon
  },
  props: {
    sensitiveLabels: { type: Boolean, default: false },
    // Label text to display before dropdown
    label: {
      type: String,
      default: null
    },
    // FontAwesome icon array, e.g. ['fas', 'calendar']
    icon: {
      type: Array,
      default: null
    },
    // Current selected value
    modelValue: {
      type: [String, Number],
      default: null
    },
    // Array of options - must be objects with { value, label }
    options: {
      type: Array,
      required: true
    },
    // Default text when no value selected
    placeholder: {
      type: String,
      default: 'No filter'
    },
    // Show a clearable "No filter" option at the top of the dropdown
    clearable: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'change'],
  data() {
    return {
      isOpen: false,
      focusedIndex: -1,
      dropdownPos: { top: 0, left: 0, minWidth: 0 }
    };
  },
  created() {
    this._uid = uid++;
  },
  computed: {
    labelId() {
      return `filter-dropdown-label-${this._uid}`;
    },
    isDisabled() {
      return !this.options || this.options.length === 0;
    },
    displayValue() {
      if (this.modelValue === null || this.modelValue === undefined) {
        return this.placeholder;
      }
      const option = this.options.find(opt => opt.value === this.modelValue);
      return option ? option.label : this.placeholder;
    },
    longestLabel() {
      if (!this.options || this.options.length === 0) return this.placeholder;
      return this.options.reduce((a, b) =>
        (a.label || '').length >= (b.label || '').length ? a : b
      ).label || this.placeholder;
    },
    dropdownStyle() {
      return {
        top: `${this.dropdownPos.top}px`,
        left: `${this.dropdownPos.left}px`,
        minWidth: `${this.dropdownPos.minWidth}px`
      };
    }
  },
  methods: {
    optionId(index) {
      return `filter-dropdown-option-${this._uid}-${index}`;
    },
    toggleDropdown() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        const selectedIndex = this.options.findIndex(opt => opt.value === this.modelValue);
        this.focusedIndex = selectedIndex >= 0 ? selectedIndex : (this.clearable ? -1 : 0);
        this.updateDropdownPosition();
      }
    },
    updateDropdownPosition() {
      const trigger = this.$refs.trigger;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      this.dropdownPos = {
        top: rect.bottom + 4,
        left: rect.left,
        minWidth: rect.width
      };
    },
    closeDropdown() {
      this.isOpen = false;
      this.focusedIndex = -1;
    },
    selectOption(option) {
      this.$emit('update:modelValue', option.value);
      this.$emit('change', option.value);
      this.closeDropdown();
      this.$refs.trigger?.focus();
    },
    handleTriggerKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          if (!this.isOpen) {
            this.toggleDropdown();
          } else {
            this.focusedIndex = Math.min(this.focusedIndex + 1, this.options.length - 1);
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (this.isOpen) {
            this.focusedIndex = Math.max(this.focusedIndex - 1, this.clearable ? -1 : 0);
          }
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (this.isOpen) {
            if (this.clearable && this.focusedIndex === -1) {
              this.selectOption({ value: null, label: this.placeholder });
            } else if (this.focusedIndex >= 0) {
              this.selectOption(this.options[this.focusedIndex]);
            }
          } else {
            this.toggleDropdown();
          }
          break;
        case 'Escape':
          if (this.isOpen) {
            event.preventDefault();
            this.closeDropdown();
            this.$refs.trigger?.focus();
          }
          break;
        case 'Home':
          if (this.isOpen) {
            event.preventDefault();
            this.focusedIndex = this.clearable ? -1 : 0;
          }
          break;
        case 'End':
          if (this.isOpen) {
            event.preventDefault();
            this.focusedIndex = this.options.length - 1;
          }
          break;
      }
    },
    handleClickOutside(event) {
      const element = this.$el;
      const listbox = this.$refs.listbox;
      if (element && !element.contains(event.target) && (!listbox || !listbox.contains(event.target))) {
        this.closeDropdown();
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  }
};
</script>

<style scoped>
.rotate-180 {
  transform: rotate(180deg);
  transition: transform 0.2s ease;
}

button svg {
  transition: transform 0.2s ease;
}
</style>
